// ============================================================================
// HAI Reporting Tool - Alert Center Component
// Displays system alerts, infections, and notifications
// ============================================================================

import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { DeviceSnapshot, HAIRecord, HCAISurveillanceCase } from '@/types';
import { aggregateHCAISurveillanceCases } from '@/utils/calculations';

export interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  wardId?: string;
  wardName?: string;
  timestamp: Date;
  metric?: string;
  currentValue?: number;
  threshold?: number;
  acknowledged?: boolean;
}

/**
 * Alert Center component that generates and displays system alerts
 */
export function AlertCenter() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAlerts();
  }, []);

  const normalizeWardName = (name?: string | null) => name?.trim().toLowerCase() || '';

  const loadAlerts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load wards first
      const wardDocs = await getDocs(collection(db, 'wards'));
      const wardMap = new Map<string, string>();
      const wardNameToId = new Map<string, string>();
      wardDocs.docs.forEach(doc => {
        wardMap.set(doc.id, doc.data().name);
        wardNameToId.set(normalizeWardName(doc.data().name), doc.id);
      });

      // Calculate date range (past 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      const todayStr = new Date().toISOString().split('T')[0];

      // Fetch device snapshots
      const deviceQuery = query(
        collection(db, 'deviceSnapshots'),
        where('date', '>=', sevenDaysAgoStr),
        where('date', '<=', todayStr)
      ) as any;

      // Fetch HAI records
      const haiQuery = query(
        collection(db, 'haiRecords'),
        where('periodEnd', '>=', sevenDaysAgoStr),
        where('periodEnd', '<=', todayStr)
      ) as any;

      const hcaiQuery = query(
        collection(db, 'hcaiCases'),
        where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo)),
        where('createdAt', '<=', Timestamp.fromDate(new Date())),
        orderBy('createdAt', 'desc')
      ) as any;

      const [deviceDocs, haiDocs, hcaiDocs] = await Promise.all([
        getDocs(deviceQuery),
        getDocs(haiQuery),
        getDocs(hcaiQuery)
      ]);

      const devices = deviceDocs.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })) as DeviceSnapshot[];
      const records = haiDocs.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })) as HAIRecord[];
      const surveillanceByWard = new Map<string, HCAISurveillanceCase[]>();

      hcaiDocs.docs.forEach(doc => {
        const data = doc.data() as any;
        const normalizedWard = normalizeWardName(data.ward);
        if (!normalizedWard) return;

        const caseRecord: HCAISurveillanceCase = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };

        if (!surveillanceByWard.has(normalizedWard)) {
          surveillanceByWard.set(normalizedWard, []);
        }
        surveillanceByWard.get(normalizedWard)!.push(caseRecord);
      });

      const generatedAlerts: Alert[] = [];

      // Check for high infection rates (CRITICAL alerts)
      const alertThresholds = {
        uti: 3.5,
        cabsi: 2.5,
        vap: 3.0
      };

      // Group records by ward and check rates
      const wardRates = new Map<string, { uti: number; cabsi: number; vap: number }>();

      records.forEach(record => {
        if (!wardRates.has(record.wardId)) {
          wardRates.set(record.wardId, { uti: 0, cabsi: 0, vap: 0 });
        }
        const rates = wardRates.get(record.wardId)!;
        rates.uti += record.cases.uti || 0;
        rates.cabsi += record.cases.cabsi || 0;
        rates.vap += record.cases.vap || 0;
      });

      // Calculate actual rates
      surveillanceByWard.forEach((cases, wardName) => {
        const wardId = wardNameToId.get(wardName);
        if (!wardId) return;

        const aggregated = aggregateHCAISurveillanceCases(cases);

        if (!wardRates.has(wardId)) {
          wardRates.set(wardId, { uti: 0, cabsi: 0, vap: 0 });
        }

        const rates = wardRates.get(wardId)!;
        rates.uti += aggregated.uti;
        rates.cabsi += aggregated.cabsi;
        rates.vap += aggregated.vap;
      });

      devices.forEach(device => {
        const deviceCatheterDays = device.patientsWithDevice?.urinaryCatheter || 0;
        const deviceCVPDays = device.patientsWithDevice?.cvp || 0;
        const deviceVentilatorDays = device.patientsWithDevice?.ventilator || 0;

        const rates = wardRates.get(device.wardId);
        if (!rates) return;

        // Calculate rates per 1000 device-days
        const utiRate = deviceCatheterDays > 0 ? (rates.uti / deviceCatheterDays) * 1000 : 0;
        const cabsiRate = deviceCVPDays > 0 ? (rates.cabsi / deviceCVPDays) * 1000 : 0;
        const vapRate = deviceVentilatorDays > 0 ? (rates.vap / deviceVentilatorDays) * 1000 : 0;

        // Generate alerts for rates above threshold
        if (utiRate > alertThresholds.uti) {
          generatedAlerts.push({
            id: `uti-${device.wardId}-${device.date}`,
            type: 'critical',
            title: '🚨 High CAUTI Rate',
            description: `CAUTI rate exceeds threshold in ${wardMap.get(device.wardId) || device.wardId}`,
            wardId: device.wardId,
            wardName: wardMap.get(device.wardId),
            timestamp: new Date(device.date),
            metric: 'CAUTI',
            currentValue: utiRate,
            threshold: alertThresholds.uti
          });
        }

        if (cabsiRate > alertThresholds.cabsi) {
          generatedAlerts.push({
            id: `cabsi-${device.wardId}-${device.date}`,
            type: 'critical',
            title: '🚨 High CLABSI Rate',
            description: `CLABSI rate exceeds threshold in ${wardMap.get(device.wardId) || device.wardId}`,
            wardId: device.wardId,
            wardName: wardMap.get(device.wardId),
            timestamp: new Date(device.date),
            metric: 'CLABSI',
            currentValue: cabsiRate,
            threshold: alertThresholds.cabsi
          });
        }

        if (vapRate > alertThresholds.vap) {
          generatedAlerts.push({
            id: `vap-${device.wardId}-${device.date}`,
            type: 'critical',
            title: '🚨 High VAP Rate',
            description: `VAP rate exceeds threshold in ${wardMap.get(device.wardId) || device.wardId}`,
            wardId: device.wardId,
            wardName: wardMap.get(device.wardId),
            timestamp: new Date(device.date),
            metric: 'VAP',
            currentValue: vapRate,
            threshold: alertThresholds.vap
          });
        }
      });

      // Check for missing data submissions (WARNING alerts)
      wardDocs.docs.forEach(wardDoc => {
        const wardId = wardDoc.id;
        const wardName = wardDoc.data().name;
        const isActive = wardDoc.data().isActive;

        if (!isActive) return;

        // Check if this ward has submitted data in the last 3 days
        const hasRecentData = devices.some(
          d => d.wardId === wardId &&
          new Date(d.date) > new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        );

        if (!hasRecentData) {
          generatedAlerts.push({
            id: `missing-data-${wardId}`,
            type: 'warning',
            title: '⚠️ Missing Data Submission',
            description: `No data submitted from ${wardName} in the last 3 days`,
            wardId,
            wardName,
            timestamp: new Date()
          });
        }
      });

      // Sort by timestamp (newest first) and limit to 10
      generatedAlerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setAlerts(generatedAlerts.slice(0, 10));
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-600">Loading alerts...</p>
          </div>
        </div>
      </div>
    );
  }

  const criticalAlerts = alerts.filter(a => a.type === 'critical');
  const warningAlerts = alerts.filter(a => a.type === 'warning');
  const infoAlerts = alerts.filter(a => a.type === 'info');

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Active Alerts</h3>
        <button
          onClick={loadAlerts}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-danger-700">{error}</p>
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-3xl mb-2">✅</div>
          <p className="text-gray-600 font-medium">No active alerts</p>
          <p className="text-sm text-gray-500">All systems operating normally</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {/* Critical Alerts */}
          {criticalAlerts.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-danger-900 mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-danger-500 text-white text-xs">
                  {criticalAlerts.length}
                </span>
                Critical
              </h4>
              <div className="space-y-2">
                {criticalAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className="bg-danger-50 border border-danger-200 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-danger-900">{alert.title}</p>
                        <p className="text-sm text-danger-700 mt-1">{alert.description}</p>
                        {alert.currentValue !== undefined && alert.threshold !== undefined && (
                          <p className="text-xs text-danger-600 mt-2">
                            Current: {alert.currentValue.toFixed(2)}/1000 | Threshold: {alert.threshold}/1000
                          </p>
                        )}
                      </div>
                      <button className="ml-2 text-danger-600 hover:text-danger-700 text-xs font-medium">
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warning Alerts */}
          {warningAlerts.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-warning-900 mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-warning-500 text-white text-xs">
                  {warningAlerts.length}
                </span>
                Warnings
              </h4>
              <div className="space-y-2">
                {warningAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className="bg-warning-50 border border-warning-200 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-warning-900">{alert.title}</p>
                        <p className="text-sm text-warning-700 mt-1">{alert.description}</p>
                      </div>
                      <button className="ml-2 text-warning-600 hover:text-warning-700 text-xs font-medium">
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Alerts */}
          {infoAlerts.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-info-900 mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-info-500 text-white text-xs">
                  {infoAlerts.length}
                </span>
                Info
              </h4>
              <div className="space-y-2">
                {infoAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className="bg-info-50 border border-info-200 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-info-900">{alert.title}</p>
                        <p className="text-sm text-info-700 mt-1">{alert.description}</p>
                      </div>
                      <button className="ml-2 text-info-600 hover:text-info-700 text-xs font-medium">
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <a href="/admin/data" className="text-xs text-primary-600 hover:text-primary-700 font-medium">
          View All Alerts & Details →
        </a>
      </div>
    </div>
  );
}
